// src/pages/Billing/BillingSuccessPage.js
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Paper,
  Chip, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { CheckCircle, WorkspacePremium, ArrowForward, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchProfile, setPremium } from '../../store/slices/authSlice';
import { usePremium } from '../../hooks/usePremium';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const COLORS = {
  forestGreen: '#2E7D32', deepForest: '#1B5E20', mediumGreen: '#4CAF50',
  charcoal: '#1A2A3A', softGreen: 'rgba(46,125,50,0.07)', offWhite: '#F8FBF8',
  border: '#E1E9F0', gradient: 'linear-gradient(135deg,#1B5E20 0%,#2E7D32 40%,#4CAF50 70%,#8BC34A 100%)',
};

const PLAN_FEATURES = {
  vendor: [
    'Unlimited direct messages per month',
    'Submit bids directly to any problem',
    '30-day solution viewer history & analytics',
    'Solutions across all industries & sectors',
    'Competitor benchmark insights',
  ],
  organization: [
    'Unlimited direct messages per month',
    'See all tagged solutions per problem',
    'AI best-match solution insight',
    'Solutions across all industries & sectors',
  ],
};

const MAX_POLLS = 12;      // up to 12 attempts
const POLL_MS = 2000;      // 2 seconds between polls
const REDIRECT_DELAY_MS = 3000;

// Helper: sleep function defined outside or at the top
const sleep = ms => new Promise(r => setTimeout(r, ms));

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(s => s.auth);
  const { refresh, isPremium, planType } = usePremium();

  const [pageStatus, setPageStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const [detectedPlan, setDetectedPlan] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(REDIRECT_DELAY_MS / 1000);
  const redirectTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    activate();
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  const activate = async () => {
    setPageStatus('loading');
    
    // Step 1: Call verify endpoint
    try {
      const { data } = await axios.post(
        `${API}/billing/verify-payment-link/`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      
      console.log('Verify response:', data);
      
      if (data.activated) {
        // Use premium_plan from backend response to match current logic
        await applyPremium(data.premium_plan, data.plan_name);
        return;
      } else {
        console.warn('Backend not activated yet:', data.error);
      }
    } catch (err) {
      console.warn('verify-payment-link error:', err);
    }

    // Step 2: Poll profile until subscription_info exists (if verify fails)
    for (let i = 0; i < MAX_POLLS; i++) {
      setAttempt(i + 1);
      await sleep(POLL_MS);
      try {
        const response = await axios.get(`${API}/auth/profile/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        const profile = response.data;
        
        // Check both subscription_info and is_premium to be safe
        if (profile.subscription_info || profile.is_premium) {
          await applyPremium(profile.premium_plan);
          return;
        }
      } catch (err) {
        console.warn('Profile poll error:', err);
      }
    }

    // Fallback: show success anyway if we ran out of polls (likely just Stripe delay)
    setPageStatus('success');
    startRedirectTimer();
  };

  const applyPremium = async (plan, planName = null) => {
    if (plan) setDetectedPlan(plan);
    
    // Update local state immediately
    dispatch(setPremium(true));
    
    // Refresh the full user object from server
    await dispatch(fetchProfile());
    
    // Refresh the custom usePremium hook state
    if (refresh) await refresh();
    
    await sleep(500);
    setPageStatus('success');
    startRedirectTimer();
  };

  const startRedirectTimer = () => {
    let seconds = REDIRECT_DELAY_MS / 1000;
    setRedirectCountdown(seconds);
    
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(() => {
      seconds -= 1;
      setRedirectCountdown(prev => (prev > 0 ? prev - 1 : 0));
      if (seconds <= 0) clearInterval(countdownIntervalRef.current);
    }, 1000);

    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    redirectTimerRef.current = setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, REDIRECT_DELAY_MS);
  };

  const handleRetry = () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    ranRef.current = false;
    setAttempt(0);
    activate();
  };

  const handleManualRedirect = () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    navigate('/dashboard', { replace: true });
  };

  // Logic to determine which features to show
  const resolvedPlan = detectedPlan || planType || user?.premium_plan || '';
  const isVendor = resolvedPlan.toLowerCase().includes('vendor');
  const planCategory = isVendor ? 'vendor' : 'organization';
  const features = PLAN_FEATURES[planCategory] || PLAN_FEATURES.organization;
  
  const planLabel = resolvedPlan
    ? resolvedPlan.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Premium';

  if (pageStatus === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: COLORS.offWhite, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, p: 3 }}>
        <CircularProgress size={56} thickness={4} sx={{ color: COLORS.forestGreen }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.charcoal }}>Activating your Premium plan…</Typography>
        {attempt > 0 && <Typography variant="body2" sx={{ color: '#6B7280' }}>Syncing with Stripe ({attempt}/{MAX_POLLS})…</Typography>}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: COLORS.offWhite, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Paper elevation={0} sx={{ maxWidth: 520, width: '100%', borderRadius: '24px', overflow: 'hidden', border: `1px solid ${COLORS.border}`, boxShadow: '0 20px 60px rgba(46,125,50,.12)' }}>
        <Box sx={{ background: COLORS.gradient, p: 4, textAlign: 'center' }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
            <WorkspacePremium sx={{ fontSize: 44, color: 'white' }} />
          </Box>
          <Typography variant="h4" sx={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, color: 'white', mb: .5 }}>You're Premium! 🎉</Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>Welcome to Rengigs Premium, {user?.first_name || user?.email?.split('@')[0]}</Typography>
        </Box>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip icon={<WorkspacePremium sx={{ color: `${COLORS.forestGreen} !important`, fontSize: 16 }} />} label={planLabel} sx={{ bgcolor: COLORS.softGreen, color: COLORS.forestGreen, fontWeight: 700 }} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.charcoal, mb: 1.5, textAlign: 'center' }}>Your plan includes:</Typography>
          <List dense disablePadding>
            {features.map(f => (
              <ListItem key={f} disableGutters sx={{ py: .4 }}>
                <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle sx={{ color: COLORS.forestGreen, fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary={f} primaryTypographyProps={{ fontSize: '.9rem', color: '#374151' }} />
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: COLORS.charcoal, fontWeight: 500 }}>Redirecting to dashboard in {redirectCountdown} seconds...</Typography>
            <Button size="small" onClick={handleManualRedirect} sx={{ mt: 1, textTransform: 'none', color: COLORS.forestGreen }}>Go now</Button>
          </Box>
          {!isPremium && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.2)' }}>
              <Typography variant="body2" sx={{ color: '#92400E', fontSize: '.82rem' }}>Your payment is confirmed. Features will activate within seconds. If your dashboard still shows Free, click "Retry" below.</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
            <Button fullWidth variant="contained" size="large" endIcon={<ArrowForward />} onClick={handleManualRedirect} sx={{ background: COLORS.gradient, color: 'white', fontWeight: 700, borderRadius: '12px', py: 1.4, textTransform: 'none' }}>Go to Dashboard Now</Button>
            {!isPremium && <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={handleRetry} sx={{ borderColor: COLORS.forestGreen, color: COLORS.forestGreen, borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>Retry Activation</Button>}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2.5, color: '#9CA3AF' }}>Receipt sent to {user?.email} · Manage subscription from your Dashboard</Typography>
        </Box>
      </Paper>
    </Box>
  );
}